import styled from "@emotion/styled";
import type { NFT } from "@stepfinance/nft";
import { ContextButton, transparentWhite } from "@stepfinance/react";
import { Card, Tooltip } from "antd";
import React, { useCallback } from "react";

import { ReactComponent as AssetBreakdownIcon } from "../../../assets/asset-breakdown-icon.svg";
import { useAppConfig } from "../../../contexts/app/config";
import { useLanguageContext } from "../../../contexts/language/language";
import { useNavigationContext } from "../../../contexts/navigation";
import { formatSolNFTPrice } from "../../../utils/nft/number-utils";
import { MonospacedText, TransparentWhiteText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { NFTCardImage } from "../NFTCardImage";

interface NFTGalleryStateFunctions {
  onImageClick(e: React.MouseEvent<HTMLElement, MouseEvent>, tokenInfo: NFT | undefined): void;
}

interface NFTCardProps extends NFTGalleryStateFunctions {
  nft: NFT;
  solPriceUsd: number;
  refToUse?: React.RefObject<HTMLImageElement>;
  canUseRef?: boolean;
  showBalances?: boolean;
}

export const NFTCard: React.FC<NFTCardProps> = ({ onImageClick, refToUse, canUseRef, nft, showBalances = false }) => {
  const { navigate } = useNavigationContext();
  const { hideableValueWrapper } = useAppConfig();

  const addFullscreenButtonMargin = showBalances && nft.amount === 1;

  const {
    currentLanguageData: {
      common,
      pages: { nftGallery: nftGalleryStrings },
    },
  } = useLanguageContext();

  const onviewItemDetails = useCallback(() => {
    navigate(`/nft/${nft.address.toBase58()}`);
  }, [navigate, nft.address]);

  return (
    <NFTGalleryCard size="small" bordered={false} cover={<NFTCardImage nft={nft} onImageClick={onImageClick} />}>
      <div>
        <p ref={canUseRef ? refToUse : null} className="nft-name">
          {nft.name}
        </p>
        <div className="metadata-rows">
          <TooltipWrapper>
            {nft.floorPrice ? (
              <>
                <PriceHeader>{hideableValueWrapper(`${formatSolNFTPrice(nft.floorPrice)} SOL`)}</PriceHeader>{" "}
                <Tooltip
                  title={
                    <CellValue cellType="userPositionValue" fontWeight="bold">
                      {nft.floorPriceUSD}
                    </CellValue>
                  }
                  placement="bottom"
                >
                  <StyledAssetBreakdownIcon />
                </Tooltip>
              </>
            ) : (
              <PriceHeader>--</PriceHeader>
            )}
          </TooltipWrapper>
          <PriceLabel>{nft.amount > 1 ? nftGalleryStrings.nftBidPrice : nftGalleryStrings.nftFloorPrice}</PriceLabel>
          <CollectionWrapper>{nft.collectionName}</CollectionWrapper>
          {nft.amount > 1 && (
            <>
              <StyledRow>
                <TransparentWhiteText>{common.balance}</TransparentWhiteText>
                <MonospacedText>{nft.amount}</MonospacedText>
              </StyledRow>
              <StyledRow>
                <TransparentWhiteText>{common.value}</TransparentWhiteText>
                <TooltipWrapper>
                  <MonospacedText>
                    {nft.floorPrice !== null && nft.floorPrice > 0
                      ? hideableValueWrapper(`${formatSolNFTPrice(nft.floorPrice)} SOL`)
                      : "--"}
                  </MonospacedText>
                  <Tooltip
                    title={
                      <CellValue cellType="userPositionValue" fontWeight="bold">
                        {nft.floorPriceUSD !== null ? nft.floorPriceUSD * nft.amount : null}
                      </CellValue>
                    }
                    placement="bottom"
                  >
                    <StyledAssetBreakdownIcon />
                  </Tooltip>
                </TooltipWrapper>
              </StyledRow>
            </>
          )}
        </div>
      </div>
      <NFTCardFullscreenButton addMargin={addFullscreenButtonMargin}>
        <ViewItemDetailsButton onClick={onviewItemDetails} data-image={nft.json?.image}>
          {nftGalleryStrings.viewItemButton}
        </ViewItemDetailsButton>
      </NFTCardFullscreenButton>
    </NFTGalleryCard>
  );
};

const NFTGalleryCard = styled(Card)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  color: #ffffff;
  font-weight: 700;
  box-sizing: border-box;

  .ant-card-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  p.nft-name {
    margin-bottom: unset;
    font-weight: bold;
    font-size: 14px;
    line-height: 24px;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
  }

  .metadata-rows {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .metadata-rows > div {
    height: 48px;
    display: flex;
    justify-content: space-between;
  }

  video {
    display: block;
  }
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  font-family: Space Mono;
`;

const PriceHeader = styled.span`
  font-size: 28px;
`;

const StyledAssetBreakdownIcon = styled(AssetBreakdownIcon)`
  margin-left: 10px;
  height: 16px;
  width: 16px;
  path {
    fill: ${transparentWhite};
  }
`;

const PriceLabel = styled.span`
  color: #7d7d7d;
  font-weight: 400;
`;

const CollectionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const NFTCardFullscreenButton = styled.div<{ addMargin: boolean }>`
  flex-grow: 1;
  display: flex;
  align-items: flex-end;
  ${(props) => {
    if (props.addMargin) {
      return "margin: 104px 0 8px 0;";
    } else {
      return "margin: 8px 0;";
    }
  }}
`;

const ViewItemDetailsButton = styled(ContextButton)`
  width: 100%;
  padding: 0;
`;
